import {
  ContractInformation,
  HealthCare_CompanyReview,
  HomeProduct,
  ProductIncentiveDetail,
  ProductSupplier,
  ScoreEntity,
  ServicesWrapper,
} from './graphProduct';
import {
  BuildingCoverage,
  CommonTechnical,
  FeaturesEdgeFeature,
  HomeCoverage,
  LiabilityCoverage,
  Package,
} from './home';

export const TYPE_UNKNOWN = 'unknown';
export const TYPE_TRIPLEPLAY = 'tripleplay';
export const TYPE_OS_TRIPLEPLAY = 'os-triple-play';
export const TYPE_HOME = 'home';
export const TYPE_ENERGY = 'energy';
export const TYPE_ENERGY_AUCTION = 'energyAuction';
export const TYPE_TRAVEL = 'travel';
export const TYPE_HEALTH = 'health';
export const DEFAULT_DISCOUNT = 10;

export const generateTypeFromCriteria = (searchCriteria: string[]) => {
  if (searchCriteria.find((s) => s.includes('filterGraphQL'))) {
    return TYPE_TRIPLEPLAY;
  }

  if (searchCriteria.find((s) => s.includes('hometype'))) {
    return TYPE_HOME;
  }

  if (searchCriteria.find((s) => s.includes('gasusage'))) {
    return TYPE_ENERGY;
  }

  if (searchCriteria.find((s) => s.includes('electricityusage'))) {
    return TYPE_ENERGY_AUCTION;
  }

  if (searchCriteria.find((s) => s.includes('insuredFamilyComposition'))) {
    return TYPE_HOME;
  }

  if (searchCriteria.find((s) => s.includes('careGivers'))) {
    return TYPE_HEALTH;
  }

  return TYPE_UNKNOWN;
};

export function getTypeFromQueryParams(params: any): IProduct['type'] {
  const searchCriteria = Array.isArray(params) ? params : Object.keys(params);

  return params.type || generateTypeFromCriteria(searchCriteria);
}

export const replaceType = (
  type: IProduct['type'] | ProductType,
  isAuctionUrl?: boolean
) => {
  if (type === 'energyAuction' || type === ProductType.ENERGYAUCTION) {
    return isAuctionUrl ? 'energy/auction' : 'energy';
  }
  return type;
};

export type BigProductFeature = {
  decimals?: number;
  hideIfZero: boolean;
  label: string;
  value: {
    month: number;
    year: number;
  };
  variant: string;
};

export type Feature = {
  id?: string;
  label: string;
  value: string;
};

export type Fee = {
  category: FeaturesEdgeFeature['category'];
  label: string;
  value: string;
};

export interface Filter {
  fields: FilterItem[];
  preserveFields?: boolean;
  id: string;
  key: string;
  minValue?: number;
  subtitle?: string;
  title: string;
  tooltip?: string;
  props: {
    labels: FilterItem[];
    minValue?: number;
  };
  type: 'checkbox' | 'radio-group' | 'select' | 'component';
}

export interface FilterItem {
  label: string;
  value: number | string;
  optionTooltip?: string;
  key?: string;
}

export interface Price {
  totalFirstYearCost?: number;
  discountIncluded: number;
  initial?: number;
  initialDiscounted?: number;
  providerDiscount?: number;
  total: number;
  postage?: number;
  connection?: number;
  priceList?: any;
  installation?: any;
  discountSource?: string;
}

export interface ProviderAttributes {
  annual_account?: {
    digital: string;
    mail: string;
  };
  edit_amount?: {
    app: string;
    online: string;
    phone: string;
  };
  meter_readings?: {
    app: string;
    online: string;
    phone: string;
  };
  payment_method?: {
    acceptgiro: string;
    automatic: string;
    iDeal: string;
  };
  postcoderoosregeling?: string;
  usage?: {
    app: string;
    online: string;
    phone: string;
  };
}
interface ConditionDetails {
  label: string;
  urlAttachment: string;
}
interface CoverageConditions {
  coverageBuildings?: ConditionDetails[];
  coverageHomes?: ConditionDetails[];
  coverageLiabilities?: ConditionDetails[];
}

export interface IProduct {
  affiliateUrl: string;
  bigProductFeature: BigProductFeature;
  comparisonId?: string;
  commonTechnical: CommonTechnical[];
  category?: string;
  characteristics?: Feature[];
  companyGroupName?: string;
  compare: boolean;
  description?: string;
  energy_label?: {
    [key: string]: number;
  };
  features?: Feature[];
  fees?: Fee[];
  filters: {
    [key: string]: any;
  };
  homePackage?: Package;
  id: string;
  productCode: string;
  links?: Link[];
  image: string;
  name?: string;
  orderType: string;
  products?: any[];
  productType?: string;
  prosAndCons: ProsAndCons;
  analyticsData?: any;
  provider: {
    attributes?: ProviderAttributes;
    description: string;
    howItWorks?: ProductSupplier['howItWorks'];
    id: string;
    externalId?: string;
    insuranceCardUrl?: HomeProduct['insuranceCardUrl'];
    label: string;
    name: string;
    postcoderoosregeling?: string;
    sellingPoints?: ProductSupplier['sellingPoints'];
    sustainabilityCommerceScore?: string;
    sustainabilityScore?: any;
    sustainabilityInvestmentScore?: string;
    switchWindowDays?: number;
    taskDivisions?: ProductSupplier['taskDivisions'];
    termsAndConditionsUrl?: HomeProduct['termsAndConditionsUrl'];
    documents?: { url: string; label: string }[];
    coverageConditions?: CoverageConditions;
    type?: string;
  };
  tags?: null | string;
  price: {
    breakdown: any;
    month: Price;
    year: Price;
  };
  priceInfo: Price;
  purchasableProduct: boolean;
  sustainabilityScore?: any;
  availableContracts?: number;
  review?: Review;
  reviews?: HealthCare_CompanyReview;
  testScore?: number;
  testScores?: any;
  type:
    | 'car'
    | 'energy'
    | 'home'
    | 'tripleplay'
    | 'os-triple-play'
    | 'energyAuction'
    | 'travel'
    | 'savings'
    | 'solarPanels'
    | 'unknown'
    | 'included'
    | 'health'
    | 'os-business-car'
    | 'moneysure';
  usps: Usp[];
  servicesWrapper?: ServicesWrapper;
  contractInformation?: ContractInformation;
  channels?: string[];
  packages?: { name: string; remarks: string; costs: any }[];
  scoreEntity?: ScoreEntity;
  subscriptionName?: string;
  benefits?: { id: number; description: string[] }[];
  notPurchasebleReason?: string;
  totalInstallmentAmount?: number;
  oneOffCostsAmount?: number;
  grossPremiumInstallment?: number;
  onceOffCosts?: any;
  incentive?: ProductIncentiveDetail;
  savingBenefits?: any;
  coverageHomes?: any;
  coverageTravels?: any;
  supplierExternalId?: any;
  yearlyGenerationInKwh?: any;
  customAttributes?: any;
  supplierCustomAttributes?: any;
  policyPolicyDetails?: any;
  garantiestelsel?: string;
  eventInformation?: any;
  durationForDiscount?: string;
  priceMonthWithDiscount?: string;
  downloadSpeedInMb?: string;
  uploadSpeedInMb?: string;
  averageDeliveryTimeInDays?: string;
  network?: string;
  tvChannels?: string;
  hdChannels?: string;
  discountThroughUs?: string;
  discountProvider?: string;
  ziggoSport?: string;
  ziggoSportPrice?: string;
  espn?: string;
  espnPrice?: string;
  combinationDiscount?: string;
  combinationDiscountInformation?: string;
  durationInMonths?: string;
  switchingService?: boolean;
  installationOptions?: boolean;
  tvViaApp?: boolean;
  reviewAndPause?: boolean;
  numberPorting?: boolean;
  unlimitedCallingInNetherlands?: string;
  unlimitedCallingInEurope?: string;
}

export type Link = {
  action: string;
  description: string;
  type: string;
  url: string;
};

export interface QuoteResponse {
  [key: string]: string | number;
}

export enum ProductType {
  CAR = 'CAR',
  ENERGY = 'ENERGY',
  HEALTH = 'HEALTH',
  HOME = 'HOME_CONTENT',
  TRIPLEPLAY = 'TRIPLE_PLAY',
  ENERGYAUCTION = 'ENERGYAUCTION',
  TRAVEL = 'TRAVEL',
  'OS-SOLAR-PANELS' = 'SOLAR_PANELS',
  'OS-HOME' = 'HOME_CONTENT',
  'CB-HEALTH' = 'HEALTH',
  'OS-HEALTH' = 'HEALTH',
  'CB-HEALTH-INTAKECAMPAIGN' = 'HEALTH',
  'OS-HEALTH-INTAKECAMPAIGN' = 'HEALTH',
  'OS-BUSINESS-CAR' = 'BUSINESS_CAR',
  'OS-TRAVEL' = 'TRAVEL',
}

export type ProsAndCons = {
  cons?: ProsAndConsItem | string[];
  pros?: ProsAndConsItem | string[];
};

export type ProsAndConsItem = {
  text1?: string;
  text2?: string;
  title1: string;
  title2: string;
};

export type Rating = {
  name: string;
  score: number;
};

export type Review = {
  averageScore: number;
  companyId: string;
  companyName: string;
  id: string;
  productType: ProductType;
  providerId: string[];
  ratings: Rating[];
  totalReviewsCount: number;
};

export interface Usp {
  text: string;
  type: string;
}

export function getHomeCoverages(homePackage: Package): {
  coverageBuildings: BuildingCoverage | null;
  coverageHomes: HomeCoverage | null;
  coverageLiabilities: LiabilityCoverage | null;
} {
  let coverages: {
    coverageBuildings: BuildingCoverage | null;
    coverageHomes: HomeCoverage | null;
    coverageLiabilities: LiabilityCoverage | null;
  } = {
    coverageBuildings: null,
    coverageHomes: null,
    coverageLiabilities: null,
  };

  if (homePackage && Array.isArray(homePackage.policyPolicyDetails)) {
    const { policyPolicyDetails } = homePackage;

    for (const pd of policyPolicyDetails) {
      for (const p of Object.keys(pd).filter((k) => {
        return k.startsWith('coverage') && Array.isArray(pd[k]);
      })) {
        coverages = {
          ...coverages,
          [p]: pd[p][0],
        };
      }
    }
  }

  return coverages;
}

export function getUspsBySellingPoints(
  sellingPoints: ProductSupplier['sellingPoints']
): Usp[] {
  return sellingPoints
    ? sellingPoints.map(({ description: text, value }) => {
        let type = 'warning';

        if (['No', 'no'].includes(value)) {
          type = 'con';
        }

        if (['Yes', 'yes'].includes(value)) {
          type = 'pro';
        }

        if (value === 'Sun') {
          type = 'sun';
        }

        return {
          text,
          type,
        };
      })
    : [];
}

export function getUspsBySellingPointsByOrderType(
  sellingPoints: ProductSupplier['sellingPoints'],
  orderType: string
): Usp[] {
  return sellingPoints
    ? sellingPoints
        .filter((point) => point.orderType === orderType)
        .map(({ description: text, value }) => {
          let type = 'warning';

          if (value === 'No') {
            type = 'con';
          }
          if (value === 'Yes') {
            type = 'pro';
          }
          if (value === 'Sun') {
            type = 'sun';
          }
          return {
            text,
            type,
          };
        })
    : [];
}

export const basicHomeIProduct: IProduct = {
  affiliateUrl: '',
  commonTechnical: [{ senderId: '', sessionId: '', transactionReference: '' }],
  orderType: '',
  bigProductFeature: {
    hideIfZero: true,
    label: '',
    value: {
      month: 0,
      year: 0,
    },
    variant: '',
  },
  companyGroupName: '',
  compare: false,
  filters: {},
  id: '',
  productCode: '',
  image: 'https://via.placeholder.com/50',
  prosAndCons: {
    cons: {
      text1: '',
      text2: '',
      title1: '',
      title2: '',
    },
    pros: {
      text1: '',
      text2: '',
      title1: '',
      title2: '',
    },
  },
  provider: {
    description: '',
    id: '',
    label: '',
    name: '',
    taskDivisions: null,
    howItWorks: null,
  },
  price: {
    breakdown: {},
    month: {
      discountIncluded: 0,
      total: 0,
    },
    year: {
      discountIncluded: 0,
      total: 0,
    },
  },
  priceInfo: {
    discountIncluded: 0,
    initial: 0,
    total: 0,
  },
  purchasableProduct: true,
  usps: [],
  type: 'home',
};

export const basicHomeProduct: HomeProduct = {
  affiliateUrl: '',
  creationDateTime: '',
  dataProviderKrn: '',
  description: '',
  entityType: '',
  externalId: '',
  id: '',
  insuranceCardUrl: '',
  krn: '',
  lastModifiedBy: '',
  lastModifiedDateTime: '',
  name: '',
  orderType: '',
  productCategory: '',
  productSupplierId: '',
  productType: '',
  productTypeKrn: '',
  refKey: '',
  state: '',
  termsAndConditionsUrl: '',
  testScores: [],
  version: 0,
  productIncentives: null,
  customAttributes: null,
  sellingPoints: null,
  eventInformation: null,
};

export const basicHomeSupplier: ProductSupplier = {
  about: '',
  addresses: null,
  advantages: [],
  creationDateTime: '',
  disadvantages: [],
  entityType: '',
  externalIds: [],
  features: null,
  howItWorks: null,
  id: '',
  informationContacts: null,
  krn: '',
  lastModifiedBy: '',
  lastModifiedDateTime: '',
  legalName: null,
  name: '',
  nameLogoUrl: '',
  porCompany: '',
  refKey: '',
  sellingPoints: null,
  state: '',
  sustainabilityScore: null,
  taskDivisions: null,
  version: 0,
  customAttributes: null,
};

export const getEnergyTypeBaseOnUsage = (
  electricityUsageHigh: number,
  electricityUsageLow: number,
  gasusage: number
): string => {
  if (electricityUsageHigh === 0 && electricityUsageLow === 0) {
    return '3';
  }

  if (gasusage === 0 && electricityUsageHigh !== 0) {
    return '2';
  }

  return '1';
};
